import { Alert, Button } from '@mui/material';
import EmailOutlined from '@mui/icons-material/EmailOutlined';
import MarkEmailReadOutlined from '@mui/icons-material/MarkEmailReadOutlined';
import NotificationsOffOutlined from '@mui/icons-material/NotificationsOffOutlined';
import EditOutlined from '@mui/icons-material/EditOutlined';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DetailPageTemplate } from '../../components/page-templates/detail-page/DetailPageTemplate';
import { InsightTile } from '../../components/shared/tiles/InsightTile';
import { TableSection } from '../../components/shared/tables/TableSection';
import type { TableColumn } from '../../components/shared/tables/TableSection';
import CustomSwitch from '../../components/shared/CustomSwitch';
import MessagesSnackbar from '../../components/shared/MessagesSnackbar';
import useLogError from '../../hooks/useLogError';
import { NotificationPreference, notificationPreferenceClient } from '../../app/services/api/preferences/notificationPreferenceClient';

const NotificationPreferencesPage = () => {
  const { t } = useTranslation('notificationPreferences');
  const { logError } = useLogError();

  const [preferences, setPreferences] = useState<NotificationPreference[]>([]);
  const [initial, setInitial] = useState<NotificationPreference[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [savedMessage, setSavedMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const load = useCallback(async () => {
    setLoading(true);
    setLoadFailed(false);
    setErrorMessage('');
    try {
      const result = await notificationPreferenceClient.get();
      setPreferences(result);
      setInitial(result);
    } catch (error) {
      logError(error);
      setErrorMessage(t('loadFailed'));
      setLoadFailed(true);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const changed = useMemo(() => {
    return preferences.filter((p) => {
      const before = initial.find((i) => i.key === p.key);
      return before !== undefined && before.enabled !== p.enabled;
    });
  }, [preferences, initial]);

  const handleToggle = (key: string) => {
    setPreferences((previous) =>
      previous.map((p) => (p.key === key ? { ...p, enabled: !p.enabled } : p)));
  };

  const handleCancelAll = () => {
    setPreferences(initial);
  };

  const handleSaveAll = async () => {
    setSaving(true);
    setErrorMessage('');
    setSavedMessage(false);
    try {
      const result = await notificationPreferenceClient.save(changed);
      setPreferences(result);
      setInitial(result);
      setSavedMessage(true);
    } catch (error) {
      logError(error);
      setErrorMessage(t('saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  // The API label is English; prefer a per-locale name and fall back to it.
  const notificationName = (preference: NotificationPreference) =>
    t(`types.${preference.key}`, { defaultValue: preference.label });

  const enabledCount = preferences.filter((preference) => preference.enabled).length;
  const columns: TableColumn<NotificationPreference>[] = [
    { id: 'notification', label: t('notification'), render: (preference) => notificationName(preference) },
    {
      id: 'receiveEmail',
      label: t('receiveEmail'),
      align: 'right',
      render: (preference) => (
        <CustomSwitch
          checked={preference.enabled}
          disabled={saving}
          inputProps={{ 'aria-label': notificationName(preference) }}
          onChange={() => handleToggle(preference.key)}
        />
      ),
    },
  ];

  return (
    <DetailPageTemplate
      title={t('title')}
      loading={loading}
      loadingLabel={t('loading')}
      insightCountInSkeleton={4}
      actions={(
        <>
          <Button
            variant="secondary"
            size="lg"
            disabled={changed.length === 0 || saving || loadFailed}
            onClick={handleCancelAll}
          >
            {t('cancelAll')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            disabled={changed.length === 0 || saving || loadFailed}
            onClick={handleSaveAll}
          >
            {changed.length > 0 ? t('saveAllWithCount', { count: changed.length }) : t('saveAll')}
          </Button>
        </>
      )}
      headlineContent={{
        layout: 'row-four',
        insights: [
          <InsightTile
            key="available"
            label={t('available')}
            value={loadFailed ? '—' : preferences.length}
            description={null}
            icon={<EmailOutlined />}
            variant="primary"
          />,
          <InsightTile
            key="enabled"
            label={t('receivingEmail')}
            value={loadFailed ? '—' : enabledCount}
            description={null}
            icon={<MarkEmailReadOutlined />}
            variant="success"
          />,
          <InsightTile
            key="disabled"
            label={t('notReceivingEmail')}
            value={loadFailed ? '—' : preferences.length - enabledCount}
            description={null}
            icon={<NotificationsOffOutlined />}
            variant="default"
          />,
          <InsightTile
            key="unsaved"
            label={t('unsaved')}
            value={loadFailed ? '—' : changed.length}
            description={null}
            icon={<EditOutlined />}
            variant="secondary"
          />,
        ],
      }}
    >
      {loadFailed ? (
        <Alert
          severity="error"
          action={(
            <Button
              variant="secondary"
              size="md"
              onClick={load}
            >
              {t('retry')}
            </Button>
          )}
        >
          {t('loadFailed')}
        </Alert>
      ) : (
        <TableSection<NotificationPreference>
          title={t('title')}
          columns={columns}
          rows={preferences}
          getRowId={(preference) => preference.key}
          emptyMessage={t('noNotifications')}
          tableSx={{
            '& th:first-of-type, & td:first-of-type': { pl: 5 },
            '& th:last-of-type, & td:last-of-type': { pr: 5 },
          }}
        />
      )}

      <MessagesSnackbar
        open={savedMessage}
        onClose={() => setSavedMessage(false)}
        message={t('saved')}
        severity="success"
        duration={5000} />
      <MessagesSnackbar
        open={errorMessage !== ''}
        onClose={() => setErrorMessage('')}
        message={errorMessage}
        severity="error"
        duration={8000} />
    </DetailPageTemplate>
  );
};

export default NotificationPreferencesPage;
